import React, { useState, useEffect } from 'react';
import { useAuth } from '../pages/AuthContext';
import { motion } from 'framer-motion';
import { T } from './theme';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';
const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

const ArticlesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const { role } = useAuth();

  const isAdmin = (role || '').split(',').map(s => s.trim().toUpperCase()).includes('ADMIN');

  const getUserName = (email) => {
    const u = users.find(x => x.emailID === email);
    return u ? `${u.name || ''} ${u.surname || ''}`.trim() || email : email || 'Unknown';
  };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/vrv-articlecontent-getAll`);
        const data = await res.json();
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        const all = (parsed.data || []).filter(a => {
          if (a.status && a.status !== 'APPROVED') return false;
          return isAdmin || a.visibility === 'PUBLIC';
        });
        all.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setArticles(all);
        const token = localStorage.getItem('vrvToken');
        if (token) {
          const ur = await fetch(`${API_BASE}/vrv-users-getAll`, { headers: { Authorization: `Bearer ${token}` } });
          const ud = await ur.json();
          const up = typeof ud.body === 'string' ? JSON.parse(ud.body) : ud;
          setUsers(up.users || []);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchAll();
  }, [isAdmin]);

  const toggle = (id) => setExpandedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const fmtDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr), now = new Date();
    const diff = now - date;
    const mins = Math.floor(diff / 60000), hours = Math.floor(diff / 3600000), days = Math.floor(diff / 86400000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const readTime = (content) => `${Math.max(1, Math.ceil((content?.split(/\s+/).length || 0) / 200))} min read`;

  const filtered = articles.filter(a =>
    !search || [a.title, a.content, getUserName(a.createdBy)].some(v => String(v).toLowerCase().includes(search.toLowerCase()))
  );

  const kpis = [
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>, value: articles.length, label: 'Total Articles' },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>, value: new Set(articles.map(a => a.createdBy)).size, label: 'Contributors' },
  ];

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.ink }} className="">
      <section className="vrv-hero">
        <div className="vrv-container">
          <div className="relative z-10">
            <p className="vrv-tag mb-3">VRV Community</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>Articles</h1>
            <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>Spiritual wisdom and community insights from our members.</p>
            <div className="grid grid-cols-2 gap-3 max-w-lg">
              {kpis.map((k, i) => (
                <div key={i} className="vrv-kpi-card">
                  <div className="mb-1.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{k.icon}</div>
                  <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>{k.value}</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>{k.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-10">
        {/* Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <p className="text-sm" style={{ color: T.muted }}>{filtered.length} article{filtered.length !== 1 ? 's' : ''}</p>
          <div className="relative w-full sm:w-72">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: T.muted }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input type="text" placeholder="Search articles…" value={search} onChange={e => setSearch(e.target.value)} className="vrv-input pl-10" />
          </div>
        </div>

        {loading && (
          <div className="grid md:grid-cols-2 gap-6">
            {[1,2,3,4].map(i => (
              <div key={i} className="vrv-card overflow-hidden">
                <div className="shimmer h-48 w-full" />
                <div className="p-6 space-y-3">
                  <div className="shimmer h-5 w-3/4 rounded-lg" />
                  <div className="shimmer h-3 w-full rounded-lg" />
                  <div className="shimmer h-3 w-2/3 rounded-lg" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={{ show: { transition: { staggerChildren: 0.07 } } }}
            className="grid md:grid-cols-2 gap-6">
            {filtered.map((article) => {
              const isExpanded = expandedIds.includes(article.content_id);
              const isLong = article.content?.length > 300;
              const author = getUserName(article.createdBy);
              return (
                <motion.div key={article.content_id} variants={fadeUp}
                  className="vrv-card overflow-hidden flex flex-col">
                  {/* Featured image placeholder or gradient */}
                  {article.image_url ? (
                    <div className="h-44 overflow-hidden">
                      <img src={article.image_url} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    </div>
                  ) : (
                    <div className="h-2" style={{ background: `linear-gradient(90deg, ${T.navy}, ${T.gold})` }} />
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    {/* Meta */}
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {isAdmin && article.visibility === 'PRIVATE' && (
                        <span className="badge badge-muted">Private</span>
                      )}
                      <span className="badge badge-navy text-[10px]">{readTime(article.content)}</span>
                    </div>

                    <h2 className="text-lg font-bold mb-3 leading-snug" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>
                      {article.title}
                    </h2>

                    <p className={`text-sm leading-relaxed flex-1 ${!isExpanded && isLong ? 'line-clamp-4' : ''}`}
                      style={{ color: T.muted, fontFamily: 'Inter, sans-serif', whiteSpace: 'pre-wrap' }}>
                      {article.content}
                    </p>

                    {isLong && (
                      <button onClick={() => toggle(article.content_id)}
                        className="inline-flex items-center gap-1 text-xs font-bold mt-3 tracking-wide uppercase transition-colors"
                        style={{ color: T.navy, fontFamily: 'Poppins, sans-serif' }}
                        onMouseEnter={e => e.currentTarget.style.color = T.gold}
                        onMouseLeave={e => e.currentTarget.style.color = T.navy}>
                        {isExpanded ? 'Show less ↑' : 'Read more ↓'}
                      </button>
                    )}

                    {/* Footer */}
                    <div className="flex items-center gap-2 mt-4 pt-4 text-xs" style={{ borderTop: '1px solid rgba(0,0,0,0.06)', color: T.muted }}>
                      <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                        style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldDark})`, color: T.navyDark }}>
                        {author.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-semibold" style={{ color: T.ink }}>{author}</span>
                      <span>·</span>
                      <span>{fmtDate(article.createdAt)}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="vrv-card text-center py-20">
            <div className="text-5xl mb-4">📖</div>
            <p className="font-bold text-lg mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>No Articles Yet</p>
            <p className="text-sm" style={{ color: T.muted }}>Check back soon for spiritual content</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticlesPage;
